/**
 * ContextoAutenticacao - Contexto React para gerenciar autenticação
 *
 * CONTEXTO DE NEGÓCIO:
 * Gerencia estado global de autenticação (usuário, token, loading, erro)
 * e fornece funções de login, cadastro e logout para toda a aplicação.
 *
 * IMPLEMENTAÇÃO:
 * - Usa `useState` e `useEffect` para gerenciar estado e side-effects
 * - Persiste token, refresh_token e usuário em localStorage com chaves:
 *   - `@workforce-juridico:token`
 *   - `@workforce-juridico:refresh_token`
 *   - `@workforce-juridico:usuario`
 * - Valida token na inicialização usando `validarToken`
 * - Suporta refresh automático de token quando access_token expira
 */

import React, { useEffect, useState, ReactNode, useContext } from 'react';
import type {
  Usuario,
  DadosCadastro,
  RespostaLogin,
  UsuarioAutenticado,
} from '../tipos/tiposAutenticacao';
import { fazerLogin, registrarUsuario, validarToken } from '../servicos/servicoApiAutenticacao';
import { usuarioAutenticadoFromRespostaLogin } from '../tipos/tiposAutenticacao';
import { LOCALSTORAGE_TOKEN_KEY, LOCALSTORAGE_REFRESH_TOKEN_KEY, LOCALSTORAGE_USUARIO_KEY } from './authConstants';
import { ContextoAutenticacao, ValoresContextoAutenticacao } from './authContext';

interface PropriedadesProvedor {
  children: ReactNode;
}

export const ProvedorAutenticacao = ({ children }: PropriedadesProvedor): JSX.Element => {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [estaCarregando, setEstaCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);

  const estaAutenticado = !!token && !!usuario;

  // Carrega token/usuario do localStorage e (opcionalmente) valida na inicialização
  useEffect(() => {
    let isMounted = true; // Flag para evitar atualização de estado em componente desmontado

    async function restaurarSessao() {
      setEstaCarregando(true);
      try {
        const tokenSalvo = localStorage.getItem(LOCALSTORAGE_TOKEN_KEY);
        const usuarioSalvo = localStorage.getItem(LOCALSTORAGE_USUARIO_KEY);

        if (tokenSalvo && usuarioSalvo) {
          // Seta imediatamente o token/usuário para evitar exigir login a cada refresh
          if (isMounted) {
            setToken(tokenSalvo);
            try {
              setUsuario(JSON.parse(usuarioSalvo) as Usuario);
            } catch {
              setUsuario(null);
              localStorage.removeItem(LOCALSTORAGE_USUARIO_KEY);
            }
          }

          // Validação em background (otimista): se falhar, o interceptor 401 irá limpar a sessão
          try {
            const valido = await validarToken(tokenSalvo);
            if (!valido) {
              // Apenas loga; limpeza ficará a cargo do interceptor global ao receber 401 em alguma request
              if (import.meta.env.DEV) {
                 
                console.info('[auth] Token não validado no boot. Mantendo sessão otimisticamente.');
              }
            }
          } catch {
            // Ignorar erros de validação silenciosamente
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Erro restaurando sessão de autenticação', err);
          setToken(null);
          setUsuario(null);
        }
      } finally {
        if (isMounted) {
          setEstaCarregando(false);
        }
      }
    }

    restaurarSessao();

    return () => {
      isMounted = false;
    };
  }, []);

  async function login(email: string, senha: string) {
    setEstaCarregando(true);
    setErro(null);
    try {
      const resposta: RespostaLogin = await fazerLogin({ email, senha });
      const ua: UsuarioAutenticado = usuarioAutenticadoFromRespostaLogin(resposta);
      setUsuario(ua.usuario);
      setToken(ua.token);
      // Persistência (incluindo refresh_token)
      try {
        localStorage.setItem(LOCALSTORAGE_TOKEN_KEY, ua.token);
        localStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN_KEY, ua.refreshToken);
        localStorage.setItem(LOCALSTORAGE_USUARIO_KEY, JSON.stringify(ua.usuario));
      } catch (e) {
        console.warn('Não foi possível persistir credenciais no localStorage', e);
      }
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Erro desconhecido ao realizar login');
      throw err;
    } finally {
      setEstaCarregando(false);
    }
  }

  async function cadastrar(dados: DadosCadastro) {
    setEstaCarregando(true);
    setErro(null);
    try {
      // Registra e faz login automaticamente com as mesmas credenciais
      await registrarUsuario(dados);
      // Depois do registro, efetua login
      await login(dados.email, dados.senha);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Erro desconhecido ao cadastrar usuário');
      throw err;
    } finally {
      setEstaCarregando(false);
    }
  }

  function logout() {
    setUsuario(null);
    setToken(null);
    setErro(null);
    try {
      localStorage.removeItem(LOCALSTORAGE_TOKEN_KEY);
      localStorage.removeItem(LOCALSTORAGE_REFRESH_TOKEN_KEY);
      localStorage.removeItem(LOCALSTORAGE_USUARIO_KEY);
    } catch (e) {
      console.warn('Falha ao limpar localStorage durante logout', e);
    }
  }

  const valor: ValoresContextoAutenticacao = {
    usuario,
    token,
    estaAutenticado,
    estaCarregando,
    erro,
    login,
    cadastrar,
    logout,
  };

  return (
    <ContextoAutenticacao.Provider value={valor}>
      {children}
    </ContextoAutenticacao.Provider>
  );
}

export const useAutenticacao = () => { // eslint-disable-line react-refresh/only-export-components
  const context = useContext(ContextoAutenticacao);
  if (context === undefined) {
    throw new Error('useAutenticacao must be used within a ProvedorAutenticacao');
  }
  return context;
};


